import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { TabBar } from './components/TabBar';
import { useAuth } from './lib/auth';
import TodayPage from './routes/today';
import ChatPage from './routes/chat';
import WalkPage from './routes/walk';
import SermonNotesPage from './routes/sermon-notes';
import MemoriesPage from './routes/memories';
import MemoryDetailPage from './routes/memory-detail';
import PhotoDetailPage from './routes/photo';
import YouPage from './routes/you';
import OnboardingPage from './routes/onboarding';
import SignInPage from './routes/sign-in';
import JournalPage from './routes/journal';
import AlertsPage from './routes/alerts';
import GenerationsPage from './routes/generations';
import GenerationsManagePage from './routes/generations-manage';
import AdminPage from './routes/admin';
import HouseholdPage from './routes/household';
import InvitePage from './routes/invite';
import CircleInvitePage from './routes/circle-invite';
import RecapPage from './routes/recap';

class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  override state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // Surface in the console so the browser inspector can see what blew up.
    // eslint-disable-next-line no-console
    console.error('Render error:', error, info);
  }

  override render() {
    if (this.state.error) {
      return (
        <div className="min-h-full bg-paper flex items-center justify-center p-6">
          <div className="max-w-sm text-center">
            <h1 className="font-serif text-2xl text-ink">Something went wrong.</h1>
            <p className="text-soft mt-3 text-sm">
              {this.state.error.message || 'An unexpected error occurred.'}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="mt-6 h-11 px-5 rounded-xl bg-oxblood text-paper font-medium"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function App() {
  const { state } = useAuth();

  // Magic-link landings are PUBLIC: the recipient is usually signed out
  // (or on a fresh device), so we must never hide them behind the /me
  // probe's full-screen "Loading…" — that's what wedged the email link.
  // Each page resolves its own auth state internally.
  const publicRoutes = (
    <>
      <Route path="/invite/:token" element={<InvitePage />} />
      <Route path="/circle-invite/:token" element={<CircleInvitePage />} />
    </>
  );

  if (state === 'loading') {
    return (
      <ErrorBoundary>
        <Routes>
          {publicRoutes}
          <Route
            path="*"
            element={
              <div className="min-h-full flex items-center justify-center bg-paper">
                <div className="text-soft text-sm">Loading…</div>
              </div>
            }
          />
        </Routes>
      </ErrorBoundary>
    );
  }

  if (state === 'guest') {
    return (
      <ErrorBoundary>
        <Routes>
          <Route path="/onboarding" element={<OnboardingPage />} />
          {publicRoutes}
          <Route path="*" element={<SignInPage />} />
        </Routes>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <div className="min-h-full pb-[174px] bg-paper">
        <Routes>
          <Route path="/" element={<Navigate to="/today" replace />} />
          <Route path="/today" element={<TodayPage />} />
          <Route path="/chat" element={<ChatPage />} />
          <Route path="/walk" element={<WalkPage />} />
          <Route path="/walk/sermon-notes/:date?" element={<SermonNotesPage />} />
          <Route path="/memories" element={<MemoriesPage />} />
          <Route path="/memories/m/:id" element={<MemoryDetailPage />} />
          <Route path="/memories/:id" element={<PhotoDetailPage />} />
          <Route path="/you" element={<YouPage />} />
          <Route path="/alerts" element={<AlertsPage />} />
          <Route path="/generations" element={<GenerationsPage />} />
          <Route path="/generations/manage" element={<GenerationsManagePage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/household" element={<HouseholdPage />} />
          {publicRoutes}
          <Route path="/journal" element={<JournalPage />} />
          <Route path="/recap" element={<RecapPage />} />
          <Route path="/recap/:year" element={<RecapPage />} />
          {/* Redirects for renamed routes. Must come before the catch-all. */}
          <Route path="/prayer" element={<Navigate to="/walk" replace />} />
          <Route path="/circles" element={<Navigate to="/generations" replace />} />
          <Route path="/settings" element={<Navigate to="/you" replace />} />
          <Route path="*" element={<Navigate to="/today" replace />} />
        </Routes>
        <TabBar />
      </div>
    </ErrorBoundary>
  );
}
